<template>
  <form @submit="submit">
    <el-row :gutter="8" type="flex" :justify="justify" :align="align">
      <template v-for="(fieldSchema, fieldName) in mainFormSchema" :key="fieldName">
        <el-col :span="fieldSchema.width ? fieldSchema.width : 24">
          <BaseInput
            :key="fieldName"
            :fieldName="fieldName"
            v-model="formModel[fieldName]"
            v-bind="fieldSchema"
            :error="formErrors[fieldName]"
            :isShowMultipleScan="isShowMultipleScan(fieldSchema)"
            @input="clearError(fieldName)"
            @change="handleChange(fieldName, $event)"
            @blur="validate(fieldName)"
            @uploaded="onUploaded"
            @reviewCalculation="$emit('reviewCalculation', $event)"
            @removedListString="handleRemovedListString(fieldName, $event)"
          />
        </el-col>
      </template>
    </el-row>
    <div class="flex" :ref="submitButtonRef">
      <slot name="button-section">
        <ButtonFormBuilder
          :isShownFloatingActionButton="isShownFloatingActionButton"
          :isHideSubmitButton="isHideSubmitButton"
          :buttons="combinedButtonsArray"
        >
        </ButtonFormBuilder>
      </slot>
    </div>
      <hr class="line-break" />

      <div class="mt-6">
        <ManufacturingDataBlocks />
      </div>
      <hr class="line-break" />

    <el-row v-if="hasTrailingFields" :gutter="8" type="flex" :justify="justify" :align="align">
      <template v-for="(fieldSchema, fieldName) in trailingFormSchema" :key="fieldName">
        <el-col :span="fieldSchema.width ? fieldSchema.width : 24">
          <BaseInput
            :key="fieldName"
            :fieldName="fieldName"
            v-model="formModel[fieldName]"
            v-bind="fieldSchema"
            :error="formErrors[fieldName]"
            :isShowMultipleScan="isShowMultipleScan(fieldSchema)"
            @input="clearError(fieldName)"
            @change="handleChange(fieldName, $event)"
            @blur="validate(fieldName)"
            @uploaded="onUploaded"
            @reviewCalculation="$emit('reviewCalculation', $event)"
            @removedListString="handleRemovedListString(fieldName, $event)"
          />
        </el-col>
      </template>
    </el-row>
  </form>
</template>

<script lang="ts">
import BaseInput from '@/components/BaseInput.vue'
import ButtonFormBuilder from '@/components/ButtonFormBuilder.vue'
import { UPDATE_SCANNED_UPLOAD_STATUS } from '@/store/actions'
import { LOCAL_STORAGE_ITEM } from '@/utils/constants'
import { ECustomButtonFormBuilderType, ECustomFieldType, EFileKind } from '@/utils/enums'
import { GhgEmissionCalculationType } from '@/utils/ghgEmission/GhgEmissionCalculationType'
import { GhgEmissionInput } from '@/utils/ghgEmission/GhgEmissionInput'
import { validateEmail } from '@/utils/helpers'
import { KeyenceHardKey } from '@/utils/keyenceHT/keyenceHardKeyEventHandler'
import { isEmpty } from '@/utils/typeChecker'
import { IListStringItem, TFormSchema } from '@/utils/types/commonTypes'
import { IFormInputSchema } from '@/utils/types/fieldDefinition'
import parsePhoneNumber, { CountryCode } from 'libphonenumber-js'
import { Options, Vue } from 'vue-class-component'
import { InjectReactive, Prop, Watch } from 'vue-property-decorator'
import ManufacturingDataBlocks from './ManufacturingDataBlocks.vue'

export interface IPromiseHandlers {
  resolve: Function
  reject: Function
}

export interface IFormButton {
  id: string
  nativeType: string
  label?: string
  hasNextStep?: boolean
  isShowArrow?: boolean
  type?: string
  inputType?: ECustomButtonFormBuilderType
  onClick: () => void
  options?: Array<{
    value: string
    label: string
  }>
  modelValue?: string
  isEnabledKeyenceHardKey?: boolean
  keyenceHardKey?: KeyenceHardKey
  onPressKeyenceHardKey?: () => void
}

@Options({
  components: { BaseInput, ButtonFormBuilder, ManufacturingDataBlocks },
  emits: ['submit', 'validate', 'reviewCalculation', 'back', 'removedListString'],
  name: 'FormBuilder',
})
export default class FormBuilder extends Vue {
  @InjectReactive() isShowArrow!: boolean
  @Prop({ type: Object }) readonly formSchema: TFormSchema = {}
  @Prop({ type: Object }) readonly formModel: Record<string, unknown> = {}
  @Prop({ type: Array as () => IFormButton[] }) readonly passedButtons!: IFormButton[]
  @Prop({ type: Boolean }) readonly hasNextStep?: boolean
  @Prop({ type: Boolean }) readonly isShownFloatingActionButton?: boolean
  @Prop({ type: Boolean }) readonly skipValidation?: boolean
  @Prop({ type: String }) readonly justify?: 'start' | 'end' | 'center' | 'space-around' | 'space-between' = 'start'
  @Prop({ type: String }) readonly align?: 'top' | 'middle' | 'bottom' = 'top'
  @Prop({ type: String }) readonly submitButtonRef?: string
  @Prop({ type: Boolean, default: false }) readonly isHideSubmitButton?: boolean
  @Prop({ type: Boolean, default: false }) readonly showBackButton?: boolean

  formErrors: Record<string, string> = {}

  buttonsArray: Array<IFormButton> = [
    {
      id: 'BackButton',
      nativeType: 'button',
      type: '',
      onClick: () => this.$emit('back'),
      isEnabledKeyenceHardKey: true,
      keyenceHardKey: KeyenceHardKey.BACKSPACE,
      onPressKeyenceHardKey: () => {
        this.$emit('back')
      },
    },
    {
      id: 'FormBuilder_Submit',
      nativeType: 'submit',
      label: 'OK',
      hasNextStep: this.hasNextStep,
      isShowArrow: this.isShowArrow,
      type: '',
      inputType: ECustomButtonFormBuilderType.BUTTON,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onClick: () => {},
      isEnabledKeyenceHardKey: true,
      keyenceHardKey: KeyenceHardKey.ENTER,
      onPressKeyenceHardKey: () => {
        this.$emit('submit', this.formModel)
      },
    },
  ]

  get combinedButtonsArray() {
    if (!this.passedButtons || this.passedButtons.length === 0) {
      return this.buttonsArray
    }
    return [...this.buttonsArray, ...this.passedButtons]
  }

  get modifiedFormSchema() {
    const schema = { ...this.formSchema }
    if (schema.machine) {
      schema.machine = { ...schema.machine, readonly: true }
    }
    if (schema.sirial && schema.machine) {
      const { sirial, ...rest } = schema
      return { sirial, ...rest }
    }
    return schema
  }

  get mainFormSchema() {
    const schema = { ...this.modifiedFormSchema }
    if (schema.sirial && schema.machine) {
      delete schema.machine
    }
    return schema
  }

  get trailingFormSchema(): TFormSchema {
    const schema = this.modifiedFormSchema
    return schema.sirial && schema.machine ? { machine: schema.machine } : {}
  }

  get hasTrailingFields() {
    return !isEmpty(this.trailingFormSchema)
  }

  get isValidated() {
    return Object.values(this.formErrors).reduce((acc, curValue) => acc && curValue === '', true)
  }

  cbTasks: IPromiseHandlers[] = []

  scanBCKeys: string[] = []
  created() {
    if (!this.showBackButton && this.formSchema && Object.keys(this.formSchema).length > 0) {
      this.buttonsArray = this.buttonsArray.filter((button) => button.id !== 'BackButton')
    }

    this.resetError()

    this.scanBCKeys = Object.keys(this.formSchema).filter((key: string) =>
      this.isBarcodeScanFrame(this.formSchema[key].fileKind)
    )

    if (this.scannedWorkCenterCode() && this.formModel && !this.formModel.machine) {
      this.formModel.machine = this.scannedWorkCenterCode()
    }
  }

  resetError() {
    if (this.formModel) {
      Object.keys(this.formModel).forEach((f: string) => (this.formErrors[f] = ''), this)
    }
  }

  onUploaded(uploadedResponse: { url: string }[]) {
    if (uploadedResponse?.length !== 0 && !isEmpty(this.cbTasks)) {
      this.cbTasks[this.cbTasks.length - 1].resolve()
      if (uploadedResponse[0].url !== 'none') this.clearUploadError()
    }
  }

  isShowMultipleScan(fieldSchema: IFormInputSchema) {
    let result = false
    if (fieldSchema && fieldSchema.cameraFeatures?.bulkCameraScanResultSetting?.isEnable) {
      result = true
    }
    return result
  }

  clearUploadError() {
    this.scanBCKeys.forEach((key) => this.clearError(key))
  }

  isBarcodeScanFrame(v?: string): boolean {
    return v === EFileKind.BARCODE_SCAN_FRAME
  }

  submit(e: Event) {
    e.preventDefault()
    this.validateAll()
    const isWaitUpload = !isEmpty(this.scanBCKeys)

    // Validate other fields if have
    this.$emit('validate')

    const submitFunc = () => {
      if (!this.skipValidation && !this.isValidated) return
      this.$emit('submit', this.formModel)
    }

    if (!isWaitUpload) submitFunc()
    else this.performUpload(() => submitFunc())
    this.scrollToError()
  }

  // validate all except scan-bc-frame field
  validateAll() {
    this.resetError()
    Object.keys(this.formSchema)
      .filter((key: string) => !this.scanBCKeys.includes(key))
      .forEach((key: string) => this.validate(key))
    return Object.values(this.formErrors).filter((err) => err).length === 0
  }

  validateBCScanFields() {
    Object.keys(this.formSchema)
      .filter((key: string) => this.scanBCKeys.includes(key))
      .forEach((key: string) => this.validate(key))
    return Object.values(this.formErrors).filter((err) => err).length === 0
  }

  performUpload(cb: Function) {
    const performCallback = () => {
      // run validate for individual scan-bc-frame fields
      this.validateBCScanFields()

      if (this.isValidated) cb()
    }
    if (this.$store.getters.scannedBarcodeBlob && !isEmpty(this.scanBCKeys)) {
      const scanBCFields = this.scanBCKeys.reduce((total, k: string) => ({ ...total, [k]: { isReady: true } }), {})
      this.$store.commit(UPDATE_SCANNED_UPLOAD_STATUS, scanBCFields)
      new Promise((resolve, reject) => this.cbTasks.push({ resolve, reject })).then(() => performCallback())
    } else {
      performCallback()
    }
  }

  scannedWorkCenterCode() {
    return localStorage.getItem(LOCAL_STORAGE_ITEM.WORK_CENTER_CODE)
  }

  validate(key: string) {
    if (this.skipValidation) return true

    const fieldSchema = this.formSchema[key]

    // Require validation
    if (fieldSchema?.isMandatory && isEmpty(this.formModel[key])) {
      this.formErrors[key] = this.$t('Please enter', { field: fieldSchema.label })
      return false
    }

    // Min length validation
    if (fieldSchema?.minLength) {
      const val = this.formModel[key] as string
      if (val?.length < fieldSchema.minLength) {
        this.formErrors[key] = this.$t('MinLen limit', { len: fieldSchema.minLength })
        return false
      }
    }

    // Min length validation
    if (fieldSchema?.maxLength) {
      const val = this.formModel[key] as string
      if (val?.length > fieldSchema.maxLength) {
        if (fieldSchema.fieldType === ECustomFieldType.LIST_STRING) {
          this.formErrors[key] = this.$t('MaxLen limit item', { len: fieldSchema.maxLength })
        } else {
          this.formErrors[key] = this.$t('MaxLen limit', { len: fieldSchema.maxLength })
        }
        return false
      }
    }

    if (!isEmpty(this.formModel[key]) || fieldSchema.isMandatory) {
      if (fieldSchema?.fieldType === ECustomFieldType.EMAIL) {
        if (!validateEmail(this.formModel[key] as string)) {
          this.formErrors[key] = this.$t('email_format_incorrect')
          return false
        }
      }

      if (fieldSchema?.fieldType === ECustomFieldType.PHONE_NUMBER) {
        const phoneNumber = parsePhoneNumber(this.formModel[key] as string, fieldSchema.countryCode as CountryCode)
        if (phoneNumber && !phoneNumber.isValid()) {
          this.formErrors[key] = this.$t('phone_format_incorrect')
          return false
        }
      }

      if (fieldSchema?.fieldType === ECustomFieldType.GHG_EMISSION) {
        const ghgEmissionInput = this.formModel[key] as GhgEmissionInput
        const calculationType = fieldSchema.calculationType as GhgEmissionCalculationType
        let errorMessage = ''
        let isValid = true
        if (!ghgEmissionInput.transportType) {
          errorMessage += this.$t('ghgEmission.error.invalidTransportType')
          isValid = false
        }
        switch (calculationType) {
          case 'tonkilo':
            if (ghgEmissionInput.weight === undefined || ghgEmissionInput.weight <= 0) {
              errorMessage += this.$t('ghgEmission.error.invalidWeight')
              isValid = false
            }
            if (ghgEmissionInput.distance === undefined || ghgEmissionInput.distance <= 0) {
              errorMessage += this.$t('ghgEmission.error.invalidDistance')
              isValid = false
            }
            break
          case 'modifiedTonkilo':
            if (ghgEmissionInput.loadRate === undefined || ghgEmissionInput.loadRate <= 0) {
              errorMessage += this.$t('ghgEmission.error.invalidLoadRate')
              isValid = false
            }
            if (ghgEmissionInput.maxWeightCapacity === undefined || ghgEmissionInput.maxWeightCapacity <= 0) {
              errorMessage += this.$t('ghgEmission.error.invalidMaxWeightCapacity')
              isValid = false
            }
            if (ghgEmissionInput.distance === undefined || ghgEmissionInput.distance <= 0) {
              errorMessage += this.$t('ghgEmission.error.invalidDistance')
              isValid = false
            }
            break
          case 'fuelEfficiency':
            if (ghgEmissionInput.distance === undefined || ghgEmissionInput.distance <= 0) {
              errorMessage += this.$t('ghgEmission.error.invalidDistance')
              isValid = false
            }
            if (!ghgEmissionInput.vehicleUsageType) {
              errorMessage += this.$t('ghgEmission.error.invalidVehicleUsageType')
              isValid = false
            }
            break
          case 'fuel':
            if (ghgEmissionInput.fuelConsumption !== undefined && ghgEmissionInput.fuelConsumption <= 0) {
              errorMessage += this.$t('ghgEmission.error.invalidFuelConsumption')
              isValid = false
            }
            break
        }
        this.formErrors[key] = errorMessage
        return isValid
      }
    }

    this.clearError(key)
    return true
  }

  scrollToError() {
    try {
      this.$nextTick(() => {
        const errorElements = document.querySelectorAll('.error-msg')
        const firstVisibleError = Array.from(errorElements).find((element) => {
          return window.getComputedStyle(element).display !== 'none'
        })
        if (firstVisibleError) {
          firstVisibleError.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    } catch (error) {}
  }

  clearError(key: string) {
    this.formErrors[key] = ''
  }

  handleChange(fieldName: string, value: string) {
    this.clearError(fieldName)
  }

  handleRemovedListString(fieldName: string, removed: IListStringItem[]) {
    // Handle the removed list string here
    this.formModel[`removed_${fieldName}`] = removed
  }

  @Watch('scannedWorkCenterCode')
  onScannedWorkCenterCodeChange(newValue: string) {
    if (newValue && this.formModel && !this.formModel.machine) {
      this.formModel.machine = newValue
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/css/mixins.scss';

form {
  text-align: center;
  padding-bottom: 24px;

  > div {
    text-align: left;
  }

  .el-row {
    flex-wrap: wrap;
  }
}
</style>
